import React, { useState, useEffect } from 'react';
import { Mic, Video, Camera, Play, X } from 'lucide-react';
import MediaCapture from '../MediaCapture';

const MediaUploader = ({ mediaFiles = [], onMediaFilesChange, disabled = false }) => {
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);
  const [isMediaCaptureOpen, setIsMediaCaptureOpen] = useState(false);
  const [previewMedia, setPreviewMedia] = useState(null); // For media preview modal

  // Get media URL from various possible fields
  const getMediaUrl = (media) => {
    return media.preview || media.file_url || media.file_url_debug || media.file;
  };

  // Generate video thumbnail from first frame using canvas
  const generateVideoThumbnail = (videoUrl, mediaId) => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.crossOrigin = 'anonymous';
      video.muted = true;
      video.playsInline = true;

      video.onloadeddata = () => {
        // Seek to 1 second (or 0 if video is shorter)
        video.currentTime = Math.min(1, video.duration * 0.1);
      };

      video.onseeked = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = video.videoWidth || 320;
          canvas.height = video.videoHeight || 240;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          const thumbnailUrl = canvas.toDataURL('image/jpeg', 0.7);

          // Update the media file with the thumbnail
          onMediaFilesChange(currentFiles =>
            currentFiles.map(f =>
              f.id === mediaId ? { ...f, thumbnail: thumbnailUrl } : f
            )
          );

          resolve(thumbnailUrl);
        } catch (err) {
          resolve(null);
        }
      };

      video.onerror = () => {
        resolve(null);
      };

      // Timeout fallback
      setTimeout(() => resolve(null), 5000);

      video.src = videoUrl;
      video.load();
    });
  };

  // Generate thumbnails for videos that don't have them
  useEffect(() => {
    mediaFiles.forEach(media => {
      if (media.media_type === 'video' && !media.thumbnail && !media.isUploading) {
        const url = getMediaUrl(media);
        if (url) {
          generateVideoThumbnail(url, media.id);
        }
      }
    });
  }, [mediaFiles]);

  // Cleanup preview URLs on unmount
  useEffect(() => {
    return () => {
      mediaFiles.forEach(media => {
        if (media.preview && media.preview.startsWith('blob:')) {
          URL.revokeObjectURL(media.preview);
        }
      });
    };
  }, []);

  // Get media type icon
  const getMediaIcon = (mediaType) => {
    switch (mediaType) {
      case 'image': return '📷';
      case 'video': return '🎥';
      case 'audio': return '🎵';
      default: return '📄';
    }
  };

  // Validate file
  const validateFile = (file, mediaType) => {
    const maxSizes = {
      image: 10 * 1024 * 1024,  // 10MB
      video: 500 * 1024 * 1024, // 100MB
      audio: 50 * 1024 * 1024   // 50MB
    };

    if (file.size > maxSizes[mediaType]) {
      throw new Error(`File size exceeds ${maxSizes[mediaType] / (1024 * 1024)}MB limit`);
    }

    return true;
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Format time duration
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle file selection - stores locally, uploads on save (like TellAStory)
  const handleFileUpload = async (file, mediaType) => {
    try {
      validateFile(file, mediaType);

      // Create preview URL
      const previewUrl = URL.createObjectURL(file);

      // Store locally - upload happens when saving the letter
      const localFile = {
        id: `local-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        file: file,
        preview: previewUrl,
        name: file.name,
        title: file.name,
        media_type: mediaType,
        file_size: file.size,
        isLocal: true // Mark as local file that needs uploading on save
      };

      // Use functional update to avoid stale closure when adding multiple files
      onMediaFilesChange(prevFiles => [...prevFiles, localFile]);

      setSuccessMessage(`${file.name} added! It will be uploaded when you save.`);
      setTimeout(() => setSuccessMessage(''), 3000);

    } catch (err) {
      console.error('File validation error:', err);
      setError(err.message);
    }
  };

  // Handle media captured from live recording
  // Stores locally - upload happens when saving the letter (like TellAStory)
  const handleMediaCaptured = (mediaData) => {
    const localFile = {
      id: `local-${Date.now()}-${Math.random()}`,
      file: mediaData.file,
      preview: mediaData.preview,
      name: mediaData.file.name,
      title: mediaData.file.name,
      media_type: mediaData.type,
      file_size: mediaData.file.size,
      duration: mediaData.duration,
      isLocal: true // Mark as local file that needs uploading on save
    };

    // Use functional update to avoid stale closure
    onMediaFilesChange(prevFiles => [...prevFiles, localFile]);

    setSuccessMessage('Recording saved! It will be uploaded when you save your letter.');
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  // Image upload handler
  const handleImageUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif';
    input.multiple = true;

    input.onchange = (e) => {
      const files = Array.from(e.target.files);
      files.forEach(file => {
        if (!file.type.startsWith('image/')) {
          setError(`Invalid file type: ${file.name}. Please select an image file.`);
          return;
        }
        handleFileUpload(file, 'image');
      });
    };

    input.click();
  };

  // Video upload handler
  const handleVideoUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'video/mp4,video/avi,video/quicktime,video/wmv,video/webm,video/x-matroska,.mov,.mkv';
    input.multiple = true;

    input.onchange = (e) => {
      const files = Array.from(e.target.files);
      files.forEach(file => {
        if (!file.type.startsWith('video/')) {
          setError(`Invalid file type: ${file.name}. Please select a video file.`);
          return;
        }
        handleFileUpload(file, 'video');
      });
    };

    input.click();
  };

  // Audio upload handler
  const handleAudioUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'audio/mp3,audio/wav,audio/m4a,audio/aac,audio/ogg,audio/flac,audio/mpeg';
    input.multiple = true;

    input.onchange = (e) => {
      const files = Array.from(e.target.files);
      files.forEach(file => {
        if (!file.type.startsWith('audio/')) {
          setError(`Invalid file type: ${file.name}. Please select an audio file.`);
          return;
        }
        handleFileUpload(file, 'audio');
      });
    };

    input.click();
  };

  // Handle remove media
  const handleRemoveMedia = (mediaId) => {
    const fileToRemove = mediaFiles.find(f => f.id === mediaId);
    if (fileToRemove && fileToRemove.preview && fileToRemove.preview.startsWith('blob:')) {
      URL.revokeObjectURL(fileToRemove.preview);
    }
    const updatedFiles = mediaFiles.filter(f => f.id !== mediaId);
    onMediaFilesChange(updatedFiles);
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);

    if (disabled) return;

    const files = Array.from(e.dataTransfer.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));

    if (imageFiles.length === 0) {
      setError('Please drop only image files.');
      return;
    }

    imageFiles.forEach(file => {
      if (file.size > 10 * 1024 * 1024) {
        setError(`Image too large: ${file.name}. Maximum size is 10MB.`);
        return;
      }
      handleFileUpload(file, 'image');
    });
  };

  return (
    <div className="space-y-6">
      {/* Error and Success Messages */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex justify-between items-center">
          {error}
          <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
            <X size={16} />
          </button>
        </div>
      )}

      {successMessage && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
          {successMessage}
        </div>
      )}

      {/* Media Capture Options */}
      <div>
        <h4 className="text-lg font-semibold mb-4 text-gray-700">
          How would you like to add media?
        </h4>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Record Media */}
          <button
            onClick={() => setIsMediaCaptureOpen(true)}
            disabled={disabled}
            className="p-5 rounded-xl border-2 border-gray-300 hover:border-green-500 hover:shadow-lg bg-white transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed group"
          >
            <div className="flex flex-col items-center">
              <div className="relative mb-3">
                <Video className="w-10 h-10 text-green-600 group-hover:text-green-700 transition-colors" />
                <Mic className="w-6 h-6 text-green-500 absolute -bottom-1 -right-1 group-hover:text-green-600 transition-colors" />
              </div>
              <div className="font-semibold text-base mb-1 text-gray-800">
                Record Media
              </div>
              <div className="text-sm text-gray-500 mb-1">
                Audio or Video
              </div>
              <div className="text-xs text-green-600 font-medium">
                🎙️ Live Recording
              </div>
            </div>
          </button>

          {/* Add Photos */}
          <button
            onClick={handleImageUpload}
            disabled={disabled}
            className="p-5 rounded-xl border-2 border-gray-300 hover:border-blue-500 hover:shadow-lg bg-white transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed group"
          >
            <div className="flex flex-col items-center">
              <div className="relative mb-3">
                <Camera className="w-10 h-10 text-blue-600 group-hover:text-blue-700 transition-colors" />
                <div className="absolute -top-1 -right-1 w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-xs text-blue-600 font-bold">+</span>
                </div>
              </div>
              <div className="font-semibold text-base mb-1 text-gray-800">
                Add Photos
              </div>
              <div className="text-sm text-gray-500 mb-1">
                Upload Images
              </div>
              <div className="text-xs text-blue-600 font-medium">
                📸 Multiple Formats
              </div>
            </div>
          </button>

          {/* Add Videos */}
          <button
            onClick={handleVideoUpload}
            disabled={disabled}
            className="p-5 rounded-xl border-2 border-gray-300 hover:border-purple-500 hover:shadow-lg bg-white transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed group"
          >
            <div className="flex flex-col items-center">
              <div className="relative mb-3">
                <Video className="w-10 h-10 text-purple-600 group-hover:text-purple-700 transition-colors" />
                <div className="absolute -top-1 -right-1 w-5 h-5 bg-purple-100 rounded-full flex items-center justify-center">
                  <span className="text-xs text-purple-600 font-bold">+</span>
                </div>
              </div>
              <div className="font-semibold text-base mb-1 text-gray-800">
                Add Videos
              </div>
              <div className="text-sm text-gray-500 mb-1">
                Upload Files
              </div>
              <div className="text-xs text-purple-600 font-medium">
                🎬 MP4, AVI, MOV
              </div>
            </div>
          </button>

          {/* Add Audio */}
          <button
            onClick={handleAudioUpload}
            disabled={disabled}
            className="p-5 rounded-xl border-2 border-gray-300 hover:border-orange-500 hover:shadow-lg bg-white transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed group"
          >
            <div className="flex flex-col items-center">
              <div className="relative mb-3">
                <Mic className="w-10 h-10 text-orange-600 group-hover:text-orange-700 transition-colors" />
                <div className="absolute -top-1 -right-1 w-5 h-5 bg-orange-100 rounded-full flex items-center justify-center">
                  <span className="text-xs text-orange-600 font-bold">+</span>
                </div>
              </div>
              <div className="font-semibold text-base mb-1 text-gray-800">
                Add Audio
              </div>
              <div className="text-sm text-gray-500 mb-1">
                Upload Files
              </div>
              <div className="text-xs text-orange-600 font-medium">
                🎵 MP3, WAV, M4A
              </div>
            </div>
          </button>
        </div>
      </div>

      {/* Drag & Drop Area */}
      <div
        className={`p-6 border-2 border-dashed rounded-xl transition-all duration-200 ${
          isDragOver
            ? 'border-blue-400 bg-blue-50 shadow-lg'
            : 'border-gray-300 bg-gray-50 hover:bg-gray-100'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => !disabled && handleImageUpload()}
      >
        <div className="text-center">
          <Camera className={`w-10 h-10 mx-auto mb-3 transition-colors ${
            isDragOver ? 'text-blue-500' : 'text-gray-400'
          }`} />
          <h4 className={`text-base font-medium mb-2 transition-colors ${
            isDragOver ? 'text-blue-700' : 'text-gray-700'
          }`}>
            {isDragOver ? 'Drop Photos Here!' : 'Drag & Drop Photos Here'}
          </h4>
          <p className="text-sm text-gray-500 mb-2">
            Or click the buttons above to browse files
          </p>
          <div className="text-xs text-gray-400">
            Supports: JPEG, PNG, GIF, WebP, HEIC • Max: 10MB per image
          </div>
        </div>
      </div>

      {/* Media Preview Grid */}
      {mediaFiles.length > 0 && (
        <div>
          <h4 className="text-base font-semibold mb-3 text-gray-700">
            Attached Media ({mediaFiles.length}):
          </h4>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
            {mediaFiles.map((media) => (
              <div
                key={media.id}
                className="relative group"
              >
                <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden border border-gray-200 relative">
                  {/* Media Preview */}
                  {media.media_type === 'image' ? (
                    <img
                      src={getMediaUrl(media)}
                      alt={media.title}
                      className="w-full h-full object-cover cursor-pointer"
                      onClick={() => setPreviewMedia(media)}
                    />
                  ) : media.media_type === 'video' ? (
                    <div
                      className="w-full h-full flex flex-col items-center justify-center bg-gray-800 relative cursor-pointer"
                      onClick={() => {
                        setPreviewMedia(media);
                      }}
                    >
                      {/* Show thumbnail if available, otherwise show video element or placeholder */}
                      {media.thumbnail ? (
                        <img
                          src={media.thumbnail}
                          alt={media.title || 'Video thumbnail'}
                          className="w-full h-full object-cover"
                        />
                      ) : getMediaUrl(media) ? (
                        <video
                          src={getMediaUrl(media)}
                          className="w-full h-full object-cover"
                          muted
                          preload="metadata"
                        />
                      ) : (
                        <Video className="w-10 h-10 text-gray-400" />
                      )}
                      {/* Play button overlay */}
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors">
                        <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                          <Play className="w-6 h-6 text-gray-800 ml-1" fill="currentColor" />
                        </div>
                      </div>
                      {media.duration && (
                        <div className="absolute bottom-1 right-1 px-1.5 py-0.5 bg-black/70 text-white text-xs rounded">
                          {formatTime(media.duration)}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div
                      className="w-full h-full flex flex-col items-center justify-center bg-gray-100 cursor-pointer"
                      onClick={() => setPreviewMedia(media)}
                    >
                      <Mic className="w-8 h-8 text-gray-400" />
                      {media.duration && (
                        <div className="mt-1 px-1.5 py-0.5 bg-black/70 text-white text-xs rounded">
                          {formatTime(media.duration)}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Uploading indicator */}
                  {media.isUploading && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent"></div>
                    </div>
                  )}

                  {/* Media type badge */}
                  <div className="absolute top-1 left-1 px-1.5 py-0.5 bg-black/50 text-white text-xs rounded uppercase">
                    {media.media_type}
                  </div>

                  {/* Remove Button */}
                  {!disabled && !media.isUploading && (
                    <button
                      onClick={() => handleRemoveMedia(media.id)}
                      className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center hover:bg-red-600"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>

                {/* File info */}
                <div className="text-xs text-gray-500 mt-1 truncate">{media.title}</div>
                <div className="text-xs text-gray-400">{formatFileSize(media.file_size || 0)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* MediaCapture Modal */}
      <MediaCapture
        isOpen={isMediaCaptureOpen}
        onClose={() => setIsMediaCaptureOpen(false)}
        onMediaCaptured={handleMediaCaptured}
      />

      {/* Media Preview Modal */}
      
      {previewMedia && (
        <div
          className="fixed inset-0 bg-black/90 z-[9999] flex items-center justify-center p-4"
          onClick={() => setPreviewMedia(null)}
        >
          <button
            onClick={() => setPreviewMedia(null)}
            className="absolute top-4 right-4 text-white hover:bg-white/20 rounded-full p-2 z-10"
          >
            <X className="w-8 h-8" />
          </button>

          <div
            className="max-w-4xl max-h-[85vh] w-full"
            onClick={(e) => e.stopPropagation()}
          >
            {previewMedia.media_type === 'image' ? (
              <img
                src={getMediaUrl(previewMedia)}
                alt={previewMedia.title}
                className="max-w-full max-h-[85vh] mx-auto object-contain rounded-lg"
              />
            ) : previewMedia.media_type === 'video' ? (
              <div className="text-center">
                <video
                  src={getMediaUrl(previewMedia)}
                  controls
                  playsInline
                  className="max-w-full max-h-[75vh] mx-auto rounded-lg"
                  onError={(e) => console.error('Video load error:', e.target.error)}
                >
                  Your browser does not support the video tag.
                </video>
                <a
                  href={getMediaUrl(previewMedia)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block mt-4 px-4 py-2 bg-white text-gray-800 rounded-lg hover:bg-gray-100"
                  onClick={(e) => e.stopPropagation()}
                >
                  Open in New Tab
                </a>
              </div>
            ) : previewMedia.media_type === 'audio' ? (
              <div className="bg-gray-900 rounded-xl p-8 max-w-md mx-auto">
                <div className="text-6xl text-center mb-4">🎵</div>
                <p className="text-white text-center mb-4">{previewMedia.title}</p>
                <audio
                  src={getMediaUrl(previewMedia)}
                  controls
                  autoPlay
                  className="w-full"
                >
                  Your browser does not support the audio tag.
                </audio>
              </div>
            ) : null}

            <p className="text-white text-center mt-4 text-sm">
              {previewMedia.title}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaUploader;
